import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';

export type SemanticContentScoringOperator = 'equals' | 'notEquals' | 'atLeast' | 'atMost';

export type SemanticContentScoringTraitOption = {
    value: string;
    label: string;
};

export type SemanticContentScoringTrait = {
    key: string;
    label: string;
    displayOrder: number;
    adminVisible: boolean;
    options: SemanticContentScoringTraitOption[];
};

export type SemanticContentScoringCondition = {
    traitKey: string;
    operator: SemanticContentScoringOperator;
    value: string;
};

export type SemanticContentScoringAlternative = {
    conditions: SemanticContentScoringCondition[];
};

export type SemanticContentScoringBand = {
    score: number;
    label: string;
    alternatives: SemanticContentScoringAlternative[];
};

export type SemanticContentScoringConfiguration = {
    schemaVersion: 'semantic-content-scoring-policy-v1';
    evaluationOrder: 'highestFirst';
    minimumAssessableScore: 1;
    zeroGatePolicy: 'locked';
    traits: SemanticContentScoringTrait[];
    bands: SemanticContentScoringBand[];
};

export type SemanticContentScoringPolicyListItem = {
    semanticContentScoringPolicyId: string;
    examName: 'Academic' | 'Core';
    examTaskType: string;
    maximumContentScore: number;
    policyVersion: number;
    rubricVersion: string;
    status: 'draft' | 'published' | 'retired';
    changeSummary: string;
    lockVersion: number;
    updatedAtUtc: string;
    publishedAtUtc: string | null;
};

export type SemanticContentScoringPolicy = SemanticContentScoringPolicyListItem & {
    configuration: SemanticContentScoringConfiguration;
};

export type SemanticContentScoringValidationError = {
    ruleCode: string;
    path: string;
    message: string;
};

export type SemanticContentScoringValidationResult = {
    isValid: boolean;
    errors: SemanticContentScoringValidationError[];
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const hasVersionedApiPath = (value: string) => /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ??
            session?.accessToken ??
            import.meta.env.VITE_ADMIN_TOKEN ??
            settings.adminApiToken ??
            '',
    );
    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const value = payload as Record<string, unknown>;
        const message = value.errorMessage ?? value.message ?? value.error ?? value.title ?? value.detail;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return fallback;
}

async function requestAdmin<T>(path = '', init: RequestInit = {}): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const suffix = path ? `/${path.replace(/^\/+/, '')}` : '';
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);
    const response = await fetch(`${apiRoot}/AdminSemanticContentScoringPolicies${suffix}`, {
        ...init,
        headers,
    });
    const payload = (response.headers.get('content-type') ?? '').includes('application/json')
        ? await response.json()
        : await response.text();
    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<T>;
        if (envelope.success === false) throw new Error(getErrorMessage(envelope, 'Content scoring policy request failed'));
        return envelope.data as T;
    }
    return payload as T;
}

export const getSemanticContentScoringPolicies = () =>
    requestAdmin<SemanticContentScoringPolicyListItem[]>();

export const getSemanticContentScoringPolicy = (policyId: string) =>
    requestAdmin<SemanticContentScoringPolicy>(encodeURIComponent(policyId));

export const createSemanticContentScoringPolicyDraft = (examName: string, examTaskType: string) =>
    requestAdmin<SemanticContentScoringPolicy>(
        `${encodeURIComponent(examName)}/${encodeURIComponent(examTaskType)}/draft`,
        { method: 'POST' },
    );

export const saveSemanticContentScoringPolicyDraft = (
    policy: SemanticContentScoringPolicy,
) => requestAdmin<SemanticContentScoringPolicy | SemanticContentScoringValidationResult>(
    encodeURIComponent(policy.semanticContentScoringPolicyId),
    {
        method: 'PUT',
        body: JSON.stringify({
            rubricVersion: policy.rubricVersion,
            changeSummary: policy.changeSummary,
            lockVersion: policy.lockVersion,
            configuration: policy.configuration,
        }),
    },
);

export const validateSemanticContentScoringPolicy = (policyId: string) =>
    requestAdmin<SemanticContentScoringValidationResult>(`${encodeURIComponent(policyId)}/validate`, { method: 'POST' });

export const publishSemanticContentScoringPolicy = (policyId: string, lockVersion: number) =>
    requestAdmin<SemanticContentScoringPolicy | SemanticContentScoringValidationResult>(
        `${encodeURIComponent(policyId)}/publish`,
        { method: 'POST', body: JSON.stringify({ lockVersion }) },
    );

export function isSemanticContentScoringPolicy(
    value: SemanticContentScoringPolicy | SemanticContentScoringValidationResult,
): value is SemanticContentScoringPolicy {
    return 'configuration' in value;
}
